import { useEffect, useRef } from 'react';

import { segmentTrack } from './useTracking';

type CardVerificationDialogVariant = 'pro_trial' | 'add_on_trial' | 'hobby_verify' | 'hobby_payg';

type CardVerificationOption = 'verify_only' | 'pro_trial';

export type CardVerificationContext = {
  source:
    | 'app_details_page'
    | 'billing_page'
    | 'build_details_page'
    | 'build_hub_get_started_page'
    | 'ci_dashboard'
    | 'global_header_banner'
    | 'remote_development_environments_get_started_page'
    | 'workflow_editor';
  reason: 'build_limit_reached' | 'workflow_editor_gate' | 'trial_start' | 'manual_verify' | 'payg_enable';
  productName: 'ci' | 'build-hub' | 'remote-development-environments';
};

const cardVerificationProps = (variant: CardVerificationDialogVariant, context: CardVerificationContext) => ({
  platform: 'website',
  source: context.source,
  card_verification_reason: context.reason,
  card_verification_dialog_variant: variant,
  product_name: context.productName,
});

const trackCardVerificationDialogDisplayed = (
  variant: CardVerificationDialogVariant,
  context: CardVerificationContext,
  hasValidCardOnFile: boolean | undefined,
) => {
  segmentTrack('Card Verification Dialog Displayed', {
    ...cardVerificationProps(variant, context),
    // Left out rather than guessed when the backend didn't look the card up.
    ...(hasValidCardOnFile !== undefined && { has_valid_card_on_file: hasValidCardOnFile }),
  });
};

export const trackCardVerificationFormOpened = (
  variant: CardVerificationDialogVariant,
  context: CardVerificationContext,
  option?: CardVerificationOption,
) => {
  segmentTrack('Card Verification Form Opened', {
    ...cardVerificationProps(variant, context),
    ...(option && { card_verification_option: option }),
  });
};

type DialogDisplayedParams = {
  isOpen: boolean;
  isReady?: boolean;
  variant: CardVerificationDialogVariant;
  context: CardVerificationContext | undefined;
  hasValidCardOnFile: boolean | undefined;
};

export const useTrackCardVerificationDialogDisplayed = ({
  isOpen,
  isReady = true,
  variant,
  context,
  hasValidCardOnFile,
}: DialogDisplayedParams) => {
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasTrackedRef.current = false;
      return;
    }
    if (!isReady || !context || hasTrackedRef.current) {
      return;
    }

    hasTrackedRef.current = true;
    trackCardVerificationDialogDisplayed(variant, context, hasValidCardOnFile);
  }, [isOpen, isReady, variant, context, hasValidCardOnFile]);
};
